import { useLingui } from '@lingui/react';
import { formatDistanceToNow } from 'date-fns';
import { BellRing, UserPlus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryErrorState } from '@/components/ui/query-error-state';
import type { WorkflowItemDetailResponse } from '@/types/workflow';
import {
  getActionLabel,
  getCategoryLabel,
  getStateLabel,
  getStateTone,
  getUrgency,
} from '@/types/workflow';
import { WorkflowProgressStepper } from './WorkflowProgressStepper';
import { WorkflowPayloadDetail } from './WorkflowPayloadDetail';
import { ChangeRequestSubjectCards } from './ChangeRequestSubjectCards';
import { WorkflowTimeline } from './WorkflowTimeline';
import { ResponsibleCell } from './ResponsibleCell';
import { formatDateTime, getDateFnsLocale } from '@/lib/format';
import {
  escalationTargetLabel,
  responsibleFor,
  workflowItemPermissions,
  type ResponsibleContext,
} from '@/types/workflowResponsible';

// The label helper moved to the types module; existing imports keep working.
export { assignedRoleLabel } from '@/types/workflowRoles';

/** Categories whose payload component renders its own action buttons. */
const OWN_ACTION_CATEGORIES = new Set(['change_request', 'late_extras']);

interface WorkflowDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: WorkflowItemDetailResponse | null | undefined;
  /** The failed item load; without an item the sheet shows it with a retry instead of the skeleton. */
  error?: unknown;
  onRetry?: () => void;
  userNameMap: Map<string, string>;
  /** The sheet passes its item along: it may not be on the inbox page. */
  onActionClick: (itemId: string, action: string, currentState: string, item?: WorkflowItemDetailResponse) => void;
  /** Opens another workflow item in this sheet (a change request's follow-up case). */
  onOpenItem?: (itemId: string) => void;
  /** Who is "you" and how people are named; without it the sheet names from `userNameMap`. */
  responsibleContext?: ResponsibleContext;
  /** Opens the hand-over dialog for the item; without it the sheet offers no hand-over. */
  onHandOver?: (item: WorkflowItemDetailResponse) => void;
  /** Opens the reminder dialog for the item; without it the sheet offers no reminder. */
  onRemind?: (item: WorkflowItemDetailResponse) => void;
}

export function WorkflowDetailSheet({
  open,
  onOpenChange,
  item,
  error,
  onRetry,
  userNameMap,
  onActionClick,
  onOpenItem,
  responsibleContext,
  onHandOver,
  onRemind,
}: WorkflowDetailSheetProps) {
  const { i18n } = useLingui();

  if (!item) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        {/* The placeholder has a title and no description. */}
        <SheetContent size="lg" aria-describedby={undefined}>
          <WorkflowDetailPlaceholder error={error} onRetry={onRetry} />
        </SheetContent>
      </Sheet>
    );
  }

  const payload = (item.payload as Record<string, unknown>) ?? {};
  const urgency = getUrgency(item.due_at);
  const responsible = responsibleFor(
    item,
    responsibleContext ?? {
      namesByClerkUserId: userNameMap,
      unknownName: i18n._({ id: 'common.unknown', message: 'Unknown' }),
    },
  );
  const canHandOver = !!onHandOver && !item.completed_at;
  const canRemind = !!onRemind && !item.completed_at && workflowItemPermissions(item, responsibleContext?.userId, !!responsibleContext?.isAdmin).canRemind;
  const createdAt = item.created_at ? new Date(item.created_at) : null;
  const hasCreatedAt = createdAt !== null && !Number.isNaN(createdAt.getTime());

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent size="lg">
        <SheetHeader>
          <SheetTitle>
            {getCategoryLabel(i18n, item.category ?? '')}
          </SheetTitle>
          {hasCreatedAt ? (
            <SheetDescription title={formatDateTime(createdAt, i18n.locale)}>
              {i18n._({
                id: 'workflow.detail.createdAgo',
                message: 'Created {when}',
                values: {
                  when: formatDistanceToNow(createdAt, { addSuffix: true, locale: getDateFnsLocale(i18n.locale) }),
                },
              })}
            </SheetDescription>
          ) : (
            <SheetDescription className="sr-only">{getCategoryLabel(i18n, item.category ?? '')}</SheetDescription>
          )}
        </SheetHeader>

        <SheetBody>
        <div className="space-y-5 mt-2">
          {/* The child and the requester head a change request as clickable cards. */}
          {item.category === 'change_request' && (
            <ChangeRequestSubjectCards payload={payload} createdBy={item.created_by} userNameMap={userNameMap} />
          )}

          {/* Progress stepper */}
          <WorkflowProgressStepper currentState={item.current_state} category={item.category} />

          {/* State + urgency badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge
              tone={getStateTone(item.current_state)}
              label={getStateLabel(i18n, item.current_state ?? '')}
            />
            {urgency === 'overdue' && (
              <Badge variant="destructive">
                {i18n._({ id: 'workflow.urgency.overdue', message: 'Overdue' })}
              </Badge>
            )}
            {urgency === 'urgent' && (
              <StatusBadge tone="warning" label={i18n._({ id: 'workflow.urgency.urgent', message: 'Urgent' })} />
            )}
            {item.completed_at && (
              <StatusBadge tone="success" label={i18n._({ id: 'workflow.detail.completed', message: 'Completed' })} />
            )}
          </div>

          {/* Action buttons — prominent. change_request items render their own
              richer, state-gated triage actions (Approve / Edit-in-place / Reject /
              Mark resolved / Retry apply) inside ChangeRequestPayload below, so the
              generic available_actions buttons are suppressed for that category to
              avoid duplicate Approve/Reject controls (and to hide the internal
              execute/fail edges). late_extras (#2028) renders its two actions
              next to its list too: the primary one is not an FSM-generic action.
              All other categories keep the generic buttons. */}
          {!OWN_ACTION_CATEGORIES.has(item.category ?? '') && (item.available_actions ?? []).length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {(item.available_actions ?? []).map((action) => (
                <Button
                  key={action}
                  variant={action === 'escalate' ? 'destructive' : 'default'}
                  onClick={() =>
                    onActionClick(item.id ?? '', action, item.current_state ?? '', item)
                  }
                >
                  {getActionLabel(i18n, action)}
                </Button>
              ))}
            </div>
          )}

          <Separator />

          {/* Who has to act, the child's group, and the deadlines */}
          <div className="flex flex-wrap items-start gap-4 text-sm" data-testid="workflow-detail-meta">
            <div className="flex items-start gap-3 min-w-0">
              <ResponsibleCell
                responsible={responsible}
                escalationTargetName={escalationTargetLabel(i18n, item)}
                data-testid="workflow-detail-responsible"
              />
              {canHandOver && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => onHandOver(item)}
                  data-testid="workflow-detail-hand-over"
                >
                  <UserPlus className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                  {i18n._({ id: 'workflow.action.handOver', message: 'Hand over' })}
                </Button>
              )}
              {canRemind && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => onRemind(item)}
                  data-testid="workflow-detail-remind"
                >
                  <BellRing className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                  {i18n._({ id: 'workflow.action.remind', message: 'Remind' })}
                </Button>
              )}
            </div>
            {item.group_name && (
              <div className="flex items-start gap-2 min-w-0" data-testid="workflow-detail-group">
                <Users className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <div className="min-w-0">
                  <span className="text-xs text-muted-foreground">
                    {i18n._({ id: 'workflow.column.group', message: 'Group' })}
                  </span>
                  <p className="font-medium truncate">{item.group_name}</p>
                  {item.group_lead_name && (
                    <p className="text-xs text-muted-foreground truncate">
                      {i18n._({
                        id: 'workflow.detail.groupLead',
                        message: 'Lead: {name}',
                        values: { name: item.group_lead_name },
                      })}
                    </p>
                  )}
                </div>
              </div>
            )}
            {(item.due_at || item.completed_at) && (
              <div className="ml-auto flex gap-4 text-right">
                {item.due_at && (
                  <div>
                    <span className="text-xs text-muted-foreground">{i18n._({ id: 'workflow.column.due', message: 'Due' })}</span>
                    <p className="font-medium">{formatDateTime(item.due_at, i18n.locale)}</p>
                  </div>
                )}
                {item.completed_at && (
                  <div>
                    <span className="text-xs text-muted-foreground">
                      {i18n._({ id: 'workflow.detail.completedAt', message: 'Completed At' })}
                    </span>
                    <p className="font-medium">{formatDateTime(item.completed_at, i18n.locale)}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Payload details — category-specific rendering */}
          <div>
            <h3 className="text-sm font-semibold mb-3">
              {i18n._({ id: 'workflow.detail.details', message: 'Details' })}
            </h3>
            <WorkflowPayloadDetail
              category={item.category ?? ''}
              payload={payload}
              userNameMap={userNameMap}
              itemId={item.id}
              currentState={item.current_state}
              availableActions={item.available_actions}
              dueAt={item.due_at}
              onOpenItem={onOpenItem}
            />
          </div>

          <Separator />

          {/* Timeline */}
          <WorkflowTimeline
            transitions={item.transitions ?? []}
            userNameMap={userNameMap}
            viewerUserId={responsibleContext?.userId}
            category={item.category}
          />
        </div>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

/** The sheet before its item arrives: a skeleton of the layout, or the failed load with a retry. */
function WorkflowDetailPlaceholder({ error, onRetry }: { error?: unknown; onRetry?: () => void }) {
  const { i18n } = useLingui();
  const loadingLabel = i18n._({ id: 'workflow.detail.loading', message: 'Loading details' });

  if (error) {
    return (
      <>
        <SheetHeader>
          <SheetTitle className="sr-only">{i18n._({ id: 'workflow.detail.details', message: 'Details' })}</SheetTitle>
        </SheetHeader>
        <SheetBody>
          <QueryErrorState
            error={error}
            onRetry={onRetry}
            title={i18n._({ id: 'workflow.detail.loadFailed', message: 'Could not load this item' })}
            className="mt-6"
          />
        </SheetBody>
      </>
    );
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle className="sr-only">{loadingLabel}</SheetTitle>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-32" />
      </SheetHeader>
      <SheetBody>
        <div className="space-y-5 mt-2" data-testid="workflow-detail-skeleton" aria-busy="true">
          <Skeleton className="h-10 w-full" />
          <div className="flex gap-2">
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-6 w-16 rounded-full" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-9 w-24" />
          </div>
          <Separator />
          <div className="space-y-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-4 w-40" />
          </div>
        </div>
      </SheetBody>
    </>
  );
}
